import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { NotificationCenterKeys } from "web_myplann/Api/NotificationCenter/notificationCenterKeys";
import { RemindersKeys } from "web_myplann/Api/Reminders/RemindersKeys";
import type {
	ReminderListResponseDTO,
	ReminderResponseDTO,
	UpdateReminderResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";

// Reminders are also shown in the notification-center timeline. Those queries
// aggregate multiple item types per horizon/filter, so a mutation response
// can't be written into them directly — they have to be refetched.
const invalidateReminderTimelines = (queryClient: QueryClient) => {
	queryClient.invalidateQueries({ queryKey: NotificationCenterKeys.all });
};

export const addReminderToListCache = (
	queryClient: QueryClient,
	listKey: QueryKey,
	reminder: ReminderResponseDTO,
) => {
	queryClient.setQueryData<ReminderListResponseDTO>(
		listKey,
		(old) => old && { reminders: [...old.reminders, reminder] },
	);
	invalidateReminderTimelines(queryClient);
};

export const replaceReminderInListCaches = (
	queryClient: QueryClient,
	{ reminder, nextOccurrence }: UpdateReminderResponseDTO,
) => {
	queryClient.setQueriesData<ReminderListResponseDTO>(
		{ queryKey: RemindersKeys.lists },
		(old) => {
			if (!old?.reminders.some((item) => item.id === reminder.id)) {
				return old;
			}
			const reminders = old.reminders.map((item) =>
				item.id === reminder.id ? reminder : item,
			);
			// A solved recurring reminder comes back with its next occurrence,
			// which belongs to the same list as the original.
			if (
				nextOccurrence &&
				!reminders.some((item) => item.id === nextOccurrence.id)
			) {
				reminders.push(nextOccurrence);
			}
			return { reminders };
		},
	);
	invalidateReminderTimelines(queryClient);
};

export const removeReminderFromListCaches = (
	queryClient: QueryClient,
	id: number,
) => {
	queryClient.setQueriesData<ReminderListResponseDTO>(
		{ queryKey: RemindersKeys.lists },
		(old) =>
			old && { reminders: old.reminders.filter((item) => item.id !== id) },
	);
	invalidateReminderTimelines(queryClient);
};
